import { Button, Popover, PopoverTrigger } from '@cad-arc';
import { UpdateToEventView } from '@mark43/cad-api';
import React, { useState, MutableRefObject, useEffect, Dispatch } from 'react';
import styles from './update-to-event-button.module.css';
import { UpdateToEventPopoverContent } from './update-to-event-popover-content';
import { UpdateToEventConfirmModal } from './update-to-event-confirm-modal';
import { EntitiesToUpdate, EventAutocomplete, StylesOverride } from './types';
import classnames from 'classnames';
import { Tooltip } from '~/common/components/tooltip';

export type State =
    | {
          isPopoverOpen: false;
          isConfirmModalOpen: false;
      }
    | {
          isPopoverOpen: true;
          isConfirmModalOpen: false;
      }
    | {
          isPopoverOpen: false;
          isConfirmModalOpen: true;
      };

export type Actions =
    | { type: 'CLOSE' }
    | { type: 'SHOW_SELECT_ENTITIES_POPOVER' }
    | { type: 'SHOW_CONFIRM_UPDATE_MODAL' };

export const UpdateToEventButton = ({
    updateToEventView,
    setEventId,
    isBootstrapLoading,
    disabledMessage,
    onUpdateToEventSuccess,
    onShowEntitiesPopover,
    showEntitiesPopoverRef,
    EventAutocompleteComponent,
    stylesOverride,
    showLeadingVisualIcon,
    showTrailingVisualIcon,
    popoverState,
    popoverDispatch,
}: {
    updateToEventView?: UpdateToEventView;
    setEventId: (eventId?: number) => void;
    isBootstrapLoading: boolean;
    disabledMessage?: string;
    onUpdateToEventSuccess: () => void;
    onShowEntitiesPopover?: () => void;
    showEntitiesPopoverRef?: MutableRefObject<(() => void) | undefined>;
    EventAutocompleteComponent?: EventAutocomplete;
    stylesOverride: StylesOverride;
    showLeadingVisualIcon?: boolean;
    showTrailingVisualIcon?: boolean;
    popoverState: State;
    popoverDispatch: Dispatch<Actions>;
}) => {
    const [entitiesToUpdate, setEntitiesToUpdate] = useState<EntitiesToUpdate>();

    useEffect(() => {
        if (showEntitiesPopoverRef) {
            showEntitiesPopoverRef.current = () =>
                popoverDispatch({ type: 'SHOW_SELECT_ENTITIES_POPOVER' });
        }
    }, [showEntitiesPopoverRef, popoverDispatch]);

    useEffect(() => {
        if (popoverState.isPopoverOpen) {
            onShowEntitiesPopover?.();
        }
    }, [popoverState.isPopoverOpen, onShowEntitiesPopover]);

    return disabledMessage ? (
        <Tooltip overlay={disabledMessage}>
            <Button
                as="a"
                leadingVisual={showLeadingVisualIcon ? 'CADEvent' : undefined}
                trailingVisual={showTrailingVisualIcon ? 'TransmitData' : undefined}
                variant="ghost"
                aria-label="update to event"
                className={classnames(styles['h-flip-icon'], stylesOverride['button-ghost'])}
                data-testid="update-to-event-button"
                isDisabled={true}
            >
                Update To Event
            </Button>
        </Tooltip>
    ) : (
        <>
            <Popover isOpen={popoverState.isPopoverOpen}>
                <PopoverTrigger asChild>
                    <Button
                        as="a"
                        leadingVisual={showLeadingVisualIcon ? 'CADEvent' : undefined}
                        trailingVisual={showTrailingVisualIcon ? 'TransmitData' : undefined}
                        variant="ghost"
                        aria-label="update to event"
                        className={classnames(
                            styles['h-flip-icon'],
                            stylesOverride['button-ghost']
                        )}
                        onClick={() =>
                            popoverState.isPopoverOpen
                                ? popoverDispatch({ type: 'CLOSE' })
                                : popoverDispatch({ type: 'SHOW_SELECT_ENTITIES_POPOVER' })
                        }
                        data-testid="update-to-event-button"
                    >
                        Update To Event
                    </Button>
                </PopoverTrigger>
                <UpdateToEventPopoverContent
                    closePopover={() => popoverDispatch({ type: 'CLOSE' })}
                    openConfirmModal={(selectedEntities: EntitiesToUpdate) => {
                        setEntitiesToUpdate(selectedEntities);
                        popoverDispatch({ type: 'SHOW_CONFIRM_UPDATE_MODAL' });
                    }}
                    updateToEventView={updateToEventView}
                    setEventId={setEventId}
                    isBootstrapLoading={isBootstrapLoading}
                    EventAutocompleteComponent={EventAutocompleteComponent}
                    stylesOverride={stylesOverride}
                />
            </Popover>
            {updateToEventView && (
                <UpdateToEventConfirmModal
                    closeConfirmModal={() => popoverDispatch({ type: 'CLOSE' })}
                    entitiesToUpdate={entitiesToUpdate}
                    isConfirmModalOpen={popoverState.isConfirmModalOpen}
                    updateToEventView={updateToEventView}
                    onUpdateToEventSuccess={onUpdateToEventSuccess}
                    stylesOverride={stylesOverride}
                />
            )}
        </>
    );
};
